/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import 'react-aspect-ratio/aspect-ratio.css'
import {
  getDisplayName,
  getIsAnonymous,
  setShowMeeting,
  getMeetingInfo,
  getShowMeeting,
  getCurrentMeetingUser,
  resetLiveKitState,
  setDisplayName,
} from '../../Services/livekitReducer'
import { useAppDispatch, useAppSelector } from '../../Store/hooks'
import { LiveKitApp } from 'beemg-livekit'
import { UserType } from '../../models/livekitTypes'
import {
  useCreateRecordingMutation,
  useGetMeetingTokenQuery,
  useLazyGetStreamUrlQuery,
  useStopRecordingMutation,
  videoMeetingApi,
} from '../../Services/videoMeetingApi'
import { useNavigate } from 'react-router-dom'
import { getCurrentUser } from '../../Services/userReducer'
import { useGetSettingValue } from '../../Services/settingReducer'
import { toastInfo, toastError } from '../../Components/toast'
import { t } from 'i18next'
import { Config } from '../../Config'
import { MeetingType } from '../../models/meeting.model'
import { jwtDecode, JwtPayload } from 'jwt-decode';

interface MyCustomPayload extends JwtPayload {
  name: string;
  metadata: string;
  video: {
    roomJoin: boolean;
    room: string;
  };
}
/**
 * LiveKitMeeting component
 *
 * This component renders a LiveKit meeting when the `showMeeting` state is true.
 * It will get the meeting token and join the meeting when the meeting token is available.
 * It will also update the join time to the server when the meeting is joined.
 * It will render nothing when the `showMeeting` state is false.
 *
 * @param {boolean} showMeeting - Whether to show the meeting or not.
 * @param {MeetingInfo} meetingInfo - The meeting info object.
 * @param {boolean} isAnonymous - Whether the user is anonymous or not.
 * @param {string} displayName - The display name of the user.
 * @param {string} baseImageURL - The base URL of the image server.
 * @param {User} currentUser - The current user object.
 * @param {function} navigate - The navigate function of the react-router-dom.
 * @param {function} onEnd - The function to call when the meeting is ended.
 * @param {function} onLeave - The function to call when the user leaves the meeting.
 */
const LiveKitMeeting = () => {
  const meetingInfo = useAppSelector(getMeetingInfo)
  const showMeeting = useAppSelector(getShowMeeting)
  const dispatch = useAppDispatch(); 
  const [joinedStateSent, setJoinedStateSent] = useState(false)
  const isAnonymous = useAppSelector(getIsAnonymous)
  const displayName = useAppSelector(getDisplayName)
  const navigate = useNavigate()
  const currentMeetingUser = useAppSelector(getCurrentMeetingUser)
  const isJoinedAnonymous = currentMeetingUser?.isJoinedAnonymous
  const baseImageURL = useGetSettingValue('IMAGE_URL')
  const currentUser = useAppSelector(getCurrentUser)
  const [isGetToken, setIsGetToken] = useState(false)
  const [recordingStatus, setRecordingStatus] = useState(false);
  const recordingStatusRef = useRef(false);
  const [egressId, setEgressId] = useState<string | null>(null);

  const isHostOrCoHost =
    meetingInfo.userType === UserType.Host ||
    meetingInfo.userType === UserType.CoHost

  const token = meetingInfo.meetingToken;
  const decodedToken = useMemo(() => {
    if (token) {
      try {
        return jwtDecode<MyCustomPayload>(token);
      } catch (error) {
        console.error('Invalid token:', error);
        return null;
      }
    }
    return null;
  }, [token]);

  useEffect(() => {
    if (decodedToken?.name) {
      dispatch(setDisplayName(decodedToken.name));
    }
  }, [decodedToken, dispatch]);

  useEffect(() => {
    if (!meetingInfo.meetingToken && showMeeting && meetingInfo.meetingId) {
      setIsGetToken(true)
    } else {
      setIsGetToken(false)
    }
  }, [meetingInfo.meetingToken, showMeeting, meetingInfo.meetingId])

  const { data, isFetching, isLoading, refetch } = useGetMeetingTokenQuery(
    {
      meetingId: meetingInfo.meetingId ?? '',
      anonymous: isAnonymous,
    },
    {
      skip: !isGetToken,
      // pollingInterval: 2000,
    },
  )

  const [triggerGetStreamUrl] = useLazyGetStreamUrlQuery();
  const [createRecording] = useCreateRecordingMutation();
  const [stopRecording] = useStopRecordingMutation();

  useEffect(() => {
    const intervel = setInterval(() => {
      if (isGetToken) {
        refetch()
      }
    }, 2000)
    return () => {
      clearInterval(intervel)
    }
  }, [isGetToken])

  /**
   * Ends the meeting and sends the leave time to the server.
   * If the user is a host or cohost, navigates to the creator page.
   * If the user is not a host or cohost, navigates to the feedback page.
   * Invalidates the video meeting tags in the cache and resets the LiveKit state.
   */
  const onEnd = () => {
    setTimeout(() => {
    dispatch(
      setShowMeeting({
        showMeeting: false,
        meetingId: '',
        isAnonymous: false,
        displayName: '',
      }),
    )
    dispatch(
      videoMeetingApi.endpoints.updateEndTime.initiate({
        meeting_id: meetingInfo.meetingId,
      }),
    )
    setJoinedStateSent(false)
    if (!isHostOrCoHost && meetingInfo.meetingType !== MeetingType.ChatVideoRequest) {
     toastInfo(t('videoMeetHostEnds'), "INFO_WHITE")
     navigate(
       `/s/feedback/${meetingInfo.meetingId}/${isJoinedAnonymous ? '1' : '0'}`,
       )
      } else {
      navigate(`/s/creator`)
    }
    dispatch(videoMeetingApi.util.invalidateTags(['VideoMeeting']))
    dispatch(resetLiveKitState())
    }, 2000)
  }
  
  const getStreamUrl = async () => {
      try {
        const streamUrlResult = await triggerGetStreamUrl({
          meetingId: meetingInfo.meetingId,
          participantUserId: currentUser.id, 
        }).unwrap();
        return streamUrlResult;
      } catch (error) {
        console.error('Error fetching stream URL:', error);
        return null;
      }
  };

  /**
   * Sends the leave time to the server, invalidates the video meeting tags in the cache, and resets the LiveKit state.
   * If the user is a host or cohost, navigates to the creator page.
   * If the user is not a host or cohost, navigates to the feedback page.
   */
  const onLeave = () => {
    dispatch(
      setShowMeeting({
        showMeeting: false,
        meetingId: '',
        isAnonymous: false,
        displayName: '',
      }),
    )
    dispatch(
      videoMeetingApi.endpoints.updateLeaveTime.initiate({
        meeting_id: meetingInfo.meetingId,
      }),
    )
    setJoinedStateSent(false)
    if (!isHostOrCoHost && meetingInfo.meetingType !== MeetingType.ChatVideoRequest) {
      navigate(
        `/s/feedback/${meetingInfo.meetingId}/${isJoinedAnonymous ? '1' : '0'}`,
      )
    } else {
      navigate(`/s/creator`)
    }
    dispatch(videoMeetingApi.util.invalidateTags(['VideoMeeting']))
    dispatch(resetLiveKitState())
  }

  const handleRecording = useCallback(async (recording: boolean, meetingId: string) => {
    if (recordingStatusRef.current === recording) return; // Prevent duplicate calls

    recordingStatusRef.current = recording;
    setRecordingStatus(recording); // Only update state when needed

    try {
      if (recording) {
        // Start recording
        const response = await createRecording({ meeting_id: meetingInfo.meetingId }).unwrap();
        if (!response || !response.egressId) {
          console.warn('⚠️ Empty response or missing egressId from API');
          return;
        }

        setEgressId(response.egressId); // Store egressId in state for stopping later
        return response;
      } else {

        if (!egressId) {
          console.error("❌ No egressId found, cannot stop recording.");
          return;
        }

        // Stop recording API call
        const stopResponse = await stopRecording({ egress_id: egressId, meeting_id: meetingInfo.meetingId }).unwrap();
        // Cleanup
        setEgressId(null); // Reset egressId after stopping
      }
    } catch (error) {
      console.error('❌ Recording Error:', error);
    }
  }, [meetingInfo.meetingId, createRecording, stopRecording, egressId]);


  useEffect(() => {
    let interval: any = null

    interval = setInterval(() => {
      if (new Date().toISOString() >= meetingInfo.expectedEndTime) {
        onEnd()
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [meetingInfo.expectedEndTime])

  if (!showMeeting) {
    return null
  }

  if (!meetingInfo.actualStartTime) {
    if (
      (meetingInfo.userType === UserType.Host ||
        meetingInfo.userType === UserType.CoHost) &&
      joinedStateSent === false
    ) {
      // VideoMeetingLogs
      dispatch(
        videoMeetingApi.endpoints.updateStartTimeAPI.initiate({
          meeting_id: meetingInfo.meetingId,
          joinedName: displayName,
          joinedAnonymous: isAnonymous,
          userType: meetingInfo.userType,
        }),
      )
      setJoinedStateSent(true)
    }
    //
    return null
  }

  // TODO: check participnat time or update join time
  if (
    meetingInfo.userType !== UserType.Host &&
    meetingInfo.userType !== UserType.CoHost &&
    joinedStateSent === false
  ) {
    // VideoMeetingLogs
    dispatch(
      videoMeetingApi.endpoints.updateJoinTime.initiate({
        meeting_id: meetingInfo.meetingId,
        joinedName: displayName,
        joinedAnonymous: isAnonymous,
        userType: meetingInfo.userType,
      }),
    )
    setJoinedStateSent(true)
  }

  return (
    <LiveKitApp
      isAudioOnly={meetingInfo.isAudioMeeting}
      token={meetingInfo.meetingToken}
      endTime={new Date(meetingInfo.expectedEndTime)}
      waitingTimeForHostOrCoHost={5 * 60} // 5 minutes in seconds
      serverUrl={Config.LIVEKIT_URL}
      toast={'meeting joined'}
      meetingTitle={meetingInfo.meetingTitle}
      navigateTo={navigate}
      meetingId={meetingInfo.meetingId}
      onClickLogo={navigate}
      logo={baseImageURL + '/logo.svg' }
      profileImg={
        isAnonymous
          ? baseImageURL + '/' + currentUser.avatar
          : baseImageURL + '/' + currentUser.image[0]?.webpImageName
      }
      onMessage={(messages: any, type: any) => {
        toastInfo(messages, 'INFO_WHITE')
        if (type === 'error') {
          toastError(messages)
        }
      }}
      onEnd={() => {
        onEnd()
      }}
      onLeave={() => {
        onLeave()
      }}
      getStreamUrl={getStreamUrl}
      meetingType={meetingInfo.meetingType}
      onRecordingChange={handleRecording}
    />
  )
}

export default LiveKitMeeting
